import { EdgeProps, getStraightPath, getBezierPath } from "reactflow";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useStore } from "../App/store";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

function CrossLine(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    style = {},
    markerEnd,
    label, // Add label prop
  } = props;
  const [edgePath] = getStraightPath({
    sourceX,
    sourceY: sourceY + 10,
    targetX,
    targetY: targetY + 0,
  });

  const selectedEdgeId = useStore.getState().selectedEdgeId;
  const setSelectedEdgeId = useStore.getState().setSelectedEdgeId;
  const [isSelected, setIsSelected] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [labelInput, setLabelInput] = useState("");

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handleEdgeClick = useCallback(() => {
    setSelectedEdgeId(id);
    setIsSelected(selectedEdgeId === id);
  }, [id, selectedEdgeId]);

  // Calculate the angle of the edge to determine text orientation
  // Use useMemo to recalculate when positions change
  const labelConfig = useMemo(() => {
    const adjustedSourceY = sourceY + 10;
    const adjustedTargetY = targetY + 10;
    const dx = targetX - sourceX;
    const dy = adjustedTargetY - adjustedSourceY;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // If angle is between 90 and 270 degrees, text would be upside down
    const isUpsideDown = angle > 90 || angle < -90;

    // Calculate label position - always north/above the line
    const midX = (sourceX + targetX) / 2;
    const midY = (adjustedSourceY + adjustedTargetY) / 2;

    // Calculate perpendicular offset for spacing from line
    // Always offset upward (negative Y direction) regardless of line direction
    const length = Math.sqrt(dx * dx + dy * dy);
    const offsetDistance = 10; // Distance from line

    // For nearly horizontal lines (within 5 degrees), force rotation to 0
    const absAngle = Math.abs(angle);
    const isNearlyHorizontal = absAngle < 5 || absAngle > 175;

    let finalX, finalY, finalRotation;

    if (isNearlyHorizontal) {
      // For horizontal lines, use simple vertical offset to avoid precision issues
      finalX = midX;
      finalY = midY - offsetDistance;
      finalRotation = 0;
    } else {
      // For angled lines, use perpendicular vector calculation
      const perpX = -dy / length;
      const perpY = dx / length;

      // Always use the upward direction (negative perpY means up on screen)
      const upwardPerpX = perpY < 0 ? -perpX : perpX;
      const upwardPerpY = perpY < 0 ? -perpY : perpY;

      finalX = midX + upwardPerpX * offsetDistance;
      finalY = midY - upwardPerpY * offsetDistance;
      finalRotation = isUpsideDown ? angle + 180 : angle;
    }

    return {
      x: finalX,
      y: finalY,
      rotation: finalRotation,
    };
  }, [sourceX, sourceY, targetX, targetY]);
  return (
    <>
      <g
        className="react-flow__edge"
        onClick={handleEdgeClick}
        onDoubleClick={(event) => {
          event.stopPropagation();
          setLabelInput((label as string) || "");
          setShowDialog(true);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <path
          id={id} // Add id attribute here
          d={edgePath}
          strokeDasharray="5,5" // Dotted line pattern
          markerEnd={markerEnd}
          className="react-flow__edge-path"
        />
        {label && (
          <text
            x={labelConfig.x}
            y={labelConfig.y}
            transform={`rotate(${labelConfig.rotation}, ${labelConfig.x}, ${labelConfig.y})`}
            style={{ fontSize: 10, fill: "#666", textAnchor: "middle" }}
          >
            {label}
          </text>
        )}
      </g>
      <Dialog
        header="Enter Edge Label"
        visible={showDialog}
        style={{ width: "400px" }}
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={() => {
                if (labelInput.trim()) {
                  useStore.getState().setEdgeLabel(id, labelInput);
                }
                setShowDialog(false);
              }}
              autoFocus
            />
          </div>
        }
      >
        <div className="p-fluid">
          <InputText
            style={{ width: "95%" }}
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (labelInput.trim()) {
                  useStore.getState().setEdgeLabel(id, labelInput);
                }
                setShowDialog(false);
              }
            }}
            placeholder="Enter label text"
            autoFocus
          />
        </div>
      </Dialog>
    </>
  );
}

export default CrossLine;
